import axios from "axios";

// Operations module client — same token behaviour as the rest of the app.
const API = `${window.location.origin}/api/operations`;
const client = axios.create({ baseURL: API });
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const unwrap = (p) => p.then((r) => r.data);

/** Authenticated download: a raw <a href> would 401, so the file comes down the axios client
 *  (with the bearer token) and is handed to the browser as a blob. */
export const opsDownload = async (path, fallbackName = "download") => {
  const res = await client.get(path, { responseType: "blob" });
  const match = /filename="?([^"]+)"?/.exec(res.headers["content-disposition"] || "");
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

/** Authenticated view-in-a-tab: same reason as opsDownload — a raw <a href> carries no bearer
 *  token and 401s. `path` may be absolute (another API prefix) or relative to Operations. */
export const opsOpen = async (path) => {
  const res = await axios.get(path.startsWith("/api/") ? `${window.location.origin}${path}`
    : `${API}${path}`, {
    responseType: "blob",
    headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
  });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const opsApi = {
  meta: () => unwrap(client.get("/project-meta")),
  dashboard: () => unwrap(client.get("/dashboard")),
  savings: (year) => unwrap(client.get("/dashboard/savings", { params: { year } })),
  opsSettings: () => unwrap(client.get("/settings/permissions")),
  opsSettingsPatch: (data) => unwrap(client.patch("/settings/permissions", { data })),
  saveNotesToSelf: (id, text) => unwrap(client.put(`/projects/${id}/notes-to-self`, { text })),
  addNote: (id, text) => unwrap(client.post(`/projects/${id}/notes`, { text })),
  patchRequirements: (id, flags) => unwrap(client.patch(`/projects/${id}/requirements`, { flags })),
  saveTeam: (id, team) => unwrap(client.put(`/projects/${id}/team`, { team })),
  addTeamMember: (id, body) => unwrap(client.post(`/projects/${id}/team-members`, body)),
  billingProfile: (id) => unwrap(client.get(`/projects/${id}/billing/profile`)),
  saveBillingProfile: (id, data) => unwrap(client.put(`/projects/${id}/billing/profile`, { data })),
  saveBillingRequirements: (id, requirements, save_as_gc_default = false) =>
    unwrap(client.put(`/projects/${id}/billing/requirements`,
      { requirements, save_as_gc_default })),
  regenerateBillingCalendar: (id) =>
    unwrap(client.post(`/projects/${id}/billing/calendar/regenerate`)),
  // FIN-2 billing cycle — the live dashboard, the billing record and the two reviews
  // FIN-4 pay application package
  payAppPackage: (id, appId) =>
    unwrap(client.get(`/projects/${id}/invoicing/pay-applications/${appId}/package`)),
  payAppPackageItem: (id, appId, key, body) =>
    unwrap(client.put(`/projects/${id}/invoicing/pay-applications/${appId}/package/items/${key}`,
                      body)),
  payAppPackageUpload: (id, appId, form) =>
    unwrap(client.post(`/projects/${id}/invoicing/pay-applications/${appId}/package/upload`,
                       form)),
  payAppStoredMaterial: (id, appId, body) =>
    unwrap(client.put(
      `/projects/${id}/invoicing/pay-applications/${appId}/package/stored-material`, body)),
  payAppFilePackage: (id, appId) =>
    unwrap(client.post(`/projects/${id}/invoicing/pay-applications/${appId}/package/file`)),
  payAppMarkUploaded: (id, appId, body) =>
    unwrap(client.post(
      `/projects/${id}/invoicing/pay-applications/${appId}/package/mark-uploaded`, body)),

  // FIN-5 accounts receivable
  arDashboard: () => unwrap(client.get("/ar/dashboard")),
  arInvoice: (id) => unwrap(client.get(`/ar/invoices/${id}`)),
  arPayment: (id, body) => unwrap(client.post(`/ar/invoices/${id}/payments`, body)),
  arNote: (id, text) => unwrap(client.post(`/ar/invoices/${id}/notes`, { text })),
  arPause: (id, body) => unwrap(client.post(`/ar/invoices/${id}/pause`, body)),
  arResume: (id) => unwrap(client.post(`/ar/invoices/${id}/resume`)),
  arSettings: () => unwrap(client.get("/settings/ar")),
  arSaveSettings: (settings) => unwrap(client.put("/settings/ar", { settings })),

  // FIN-6 retention
  retention: () => unwrap(client.get("/retention")),
  projectRetention: (id) => unwrap(client.get(`/projects/${id}/retention`)),
  retentionEligibleDate: (id, body) =>
    unwrap(client.put(`/projects/${id}/retention/eligible-date`, body)),
  retentionDraftRelease: (id) =>
    unwrap(client.post(`/projects/${id}/retention/draft-release`)),

  // FIN-7 closeout
  closeoutDashboard: (sort) => unwrap(client.get("/closeout/dashboard", { params: { sort } })),
  projectCloseout: (id) => unwrap(client.get(`/projects/${id}/closeout`)),
  closeoutItem: (id, key, body) =>
    unwrap(client.put(`/projects/${id}/closeout/items/${key}`, body)),
  closeoutClose: (id, body) => unwrap(client.post(`/projects/${id}/closeout/close`, body)),

  billingDashboard: () => unwrap(client.get("/billing/dashboard")),
  billingRecord: (projectId, periodEnd) =>
    unwrap(client.get(`/billing/records/${projectId}/${periodEnd}`)),
  billingPmReview: (projectId, periodEnd, body) =>
    unwrap(client.post(`/billing/records/${projectId}/${periodEnd}/pm-review`, body)),
  billingAccountantReview: (projectId, periodEnd, body) =>
    unwrap(client.post(`/billing/records/${projectId}/${periodEnd}/accountant-review`, body)),
  billingRecordNote: (projectId, periodEnd, text) =>
    unwrap(client.post(`/billing/records/${projectId}/${periodEnd}/notes`, { text })),
  billingSettings: () => unwrap(client.get("/settings/billing")),
  saveBillingSettings: (data) => unwrap(client.put("/settings/billing", { data })),
  saveRequirementType: (body) => unwrap(client.put("/settings/billing/requirement-types", body)),
  priorityActions: (scope = "mine") =>
    unwrap(client.get("/priority-actions", { params: { scope } })),
  dismissPriorityAction: (key) => unwrap(client.post("/priority-actions/dismiss", { key })),
  emailDefaults: () => unwrap(client.get("/settings/email-defaults")),
  saveEmailDefaults: (defaults) => unwrap(client.put("/settings/email-defaults", { defaults })),
  saveEmailRouting: (id, routes) => unwrap(client.put(`/projects/${id}/email-routing`, { routes })),
  contactOptions: (id) => unwrap(client.get(`/projects/${id}/contact-options`)),
  createContact: (id, body) => unwrap(client.post(`/projects/${id}/contacts`, body)),
  listProjects: (params) => unwrap(client.get("/projects", { params })),
  getProject: (id) => unwrap(client.get(`/projects/${id}`)),
  patchProject: (id, data) => unwrap(client.patch(`/projects/${id}`, { data })),
  setStatus: (id, body) => unwrap(client.post(`/projects/${id}/status`, body)),
  permissions: (id) => unwrap(client.get(`/projects/${id}/permissions`)),
  authorize: (id, body) => unwrap(client.post(`/projects/${id}/authorize`, body)),
  events: (id, params) => unwrap(client.get(`/projects/${id}/events`, { params })),
  estimate: (id, view) => unwrap(client.get(`/projects/${id}/estimate`, { params: { view } })),
  projectEmailDraft: (id, kind) => unwrap(client.get(`/projects/${id}/email/${kind}`)),
  sendEmail: (id, body) => unwrap(client.post(`/projects/${id}/email`, body)),
  addHandoff: (id, section, body) => unwrap(client.post(`/projects/${id}/handoff/${section}`, body)),
  removeHandoff: (id, section, rowId) => unwrap(client.delete(`/projects/${id}/handoff/${section}/${rowId}`)),
  // documents (OPS-7)
  folders: () => unwrap(client.get("/documents/folders")),
  listDocuments: (id, params) => unwrap(client.get(`/projects/${id}/documents`, { params })),
  uploadDocuments: (id, files, fields = {}) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ""));
    return unwrap(client.post(`/projects/${id}/documents`, fd,
      { headers: { "Content-Type": "multipart/form-data" } }));
  },
  patchDocument: (docId, body) => unwrap(client.patch(`/documents/${docId}`, body)),
  deleteDocument: (docId) => unwrap(client.delete(`/documents/${docId}`)),
  documentVersions: (docId) => unwrap(client.get(`/documents/${docId}/versions`)),
  checklist: (id) => unwrap(client.get(`/projects/${id}/documents/checklist`)),
  openDocument: (docId) => opsOpen(`/documents/${docId}`),
  downloadDocument: (docId, name) => opsDownload(`/documents/${docId}?download=true`, name),
  documentUrl: (docId, download = false) =>
    `${window.location.origin}/api/operations/documents/${docId}${download ? "?download=true" : ""}`,
  // digital FRS (OPS-1B) — the same frs_items Field Operations uses
  frs: (id, version = "current") => unwrap(client.get(`/projects/${id}/frs`, { params: { version } })),
  frsItems: (id, params) => unwrap(client.get(`/projects/${id}/frs/items`, { params })),
  frsTree: (id, version) => unwrap(client.get(`/projects/${id}/frs/tree`, { params: { version } })),
  frsAddItem: (id, body) => unwrap(client.post(`/projects/${id}/frs/items`, body)),
  frsPatchItem: (id, itemId, body) => unwrap(client.patch(`/projects/${id}/frs/items/${itemId}`, body)),
  frsBulk: (id, body) => unwrap(client.post(`/projects/${id}/frs/items/bulk`, body)),
  frsDeleteItem: (id, itemId, reason) => unwrap(client.delete(`/projects/${id}/frs/items/${itemId}`, { params: { reason } })),
  frsVersions: (id) => unwrap(client.get(`/projects/${id}/frs/versions`)),
  frsSnapshot: (id, notes) => unwrap(client.post(`/projects/${id}/frs/versions/snapshot`, { notes })),
  frsDiff: (id, a, b) => unwrap(client.get(`/projects/${id}/frs/diff`, { params: { a, b } })),
  frsRevisions: (id, params) => unwrap(client.get(`/projects/${id}/frs/revisions`, { params })),
  frsReconcile: (id, note) => unwrap(client.post(`/projects/${id}/frs/reconcile`, { note })),
  frsUnmatched: (id) => unwrap(client.get(`/projects/${id}/frs/unmatched`)),
  frsLink: (id, body) => unwrap(client.post(`/projects/${id}/frs/link`, body)),
  frsRematch: (id) => unwrap(client.post(`/projects/${id}/frs/link/rematch`)),
  frsClearReview: (id, note) => unwrap(client.post(`/projects/${id}/frs/review/clear`, { note })),
  frsImportStaging: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return unwrap(client.post(`/projects/${id}/frs/import/staging`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  frsImportApply: (id, body) => unwrap(client.post(`/projects/${id}/frs/import/apply`, body)),
  frsExportDownload: (id) => opsDownload(`/projects/${id}/frs/export`, "FRS.xlsx"),
  // project budget (OPS-2) — generated from the working estimate, locked at Open
  budget: (id, group = "cost_code") => unwrap(client.get(`/projects/${id}/budget`, { params: { group } })),
  budgetGenerate: (id, force = false) => unwrap(client.post(`/projects/${id}/budget/generate`, { force })),
  budgetRowLines: (id, rowId) => unwrap(client.get(`/projects/${id}/budget/rows/${rowId}/lines`)),
  budgetOverride: (id, rowId, body) => unwrap(client.post(`/projects/${id}/budget/rows/${rowId}/override`, body)),
  budgetDirectCosts: (id, rowId) => unwrap(client.get(`/projects/${id}/budget/rows/${rowId}/direct-costs`)),
  budgetAddDirectCost: (id, rowId, body) => unwrap(client.post(`/projects/${id}/budget/rows/${rowId}/direct-costs`, body)),
  budgetDeleteDirectCost: (id, costId) => unwrap(client.delete(`/projects/${id}/budget/direct-costs/${costId}`)),
  // EST-QUAL-OT — the accepted schedule-acceleration option as an overtime allowance
  acceleration: (id) => unwrap(client.get(`/projects/${id}/acceleration`)),
  acceptAcceleration: (id, accepted) =>
    unwrap(client.post(`/projects/${id}/acceleration/accept`, { accepted })),
  budgetHours: (id) => unwrap(client.get(`/projects/${id}/budget/hours`)),
  budgetSnapshots: (id) => unwrap(client.get(`/projects/${id}/budget/snapshots`)),
  budgetSnapshot: (id, body) => unwrap(client.post(`/projects/${id}/budget/snapshots`, body)),
  // change orders (OPS-3)
  changeOrders: (id) => unwrap(client.get(`/projects/${id}/change-orders`)),
  changeOrder: (id, coId) => unwrap(client.get(`/projects/${id}/change-orders/${coId}`)),
  createChangeOrder: (id, body) => unwrap(client.post(`/projects/${id}/change-orders`, body)),
  patchChangeOrder: (id, coId, data) => unwrap(client.patch(`/projects/${id}/change-orders/${coId}`, { data })),
  coStatus: (id, coId, body) => unwrap(client.post(`/projects/${id}/change-orders/${coId}/status`, body)),
  coArchive: (id, coId, archived) => unwrap(client.post(`/projects/${id}/change-orders/${coId}/archive`, { archived })),
  coAddManualLine: (id, coId, body) => unwrap(client.post(`/projects/${id}/change-orders/${coId}/lines`, body)),
  coAddFromEstimate: (id, coId, lines) => unwrap(client.post(`/projects/${id}/change-orders/${coId}/lines/from-estimate`, { lines })),
  coAddFromFrs: (id, coId, items) => unwrap(client.post(`/projects/${id}/change-orders/${coId}/lines/from-frs`, { items })),
  coAddFromFrsDiff: (id, coId, body) => unwrap(client.post(`/projects/${id}/change-orders/${coId}/lines/from-frs-diff`, body)),
  coPatchLine: (id, coId, lineId, fields) => unwrap(client.patch(`/projects/${id}/change-orders/${coId}/lines/${lineId}`, { fields })),
  coDeleteLine: (id, coId, lineId) => unwrap(client.delete(`/projects/${id}/change-orders/${coId}/lines/${lineId}`)),
  coFrsProposals: (id, coId) => unwrap(client.get(`/projects/${id}/change-orders/${coId}/frs/proposals`)),
  coFrsSync: (id, coId, body) => unwrap(client.post(`/projects/${id}/change-orders/${coId}/frs/sync`, body)),
  coUploadDocument: (id, coId, form) => unwrap(client.post(`/projects/${id}/change-orders/${coId}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } })),
  // RFI / COR / ASI intake (RFI-1/2)
  intakeQueue: () => unwrap(client.get("/intake")),
  projectIntake: (id) => unwrap(client.get(`/projects/${id}/intake`)),
  intakeDetail: (itemId) => unwrap(client.get(`/intake/${itemId}`)),
  intakeManual: (body) => unwrap(client.post("/intake/manual", body)),
  intakeUpload: (form) => unwrap(client.post("/intake/upload", form, { headers: { "Content-Type": "multipart/form-data" } })),
  intakeReview: (itemId, body) => unwrap(client.post(`/intake/${itemId}/review`, body)),
  intakeAssign: (itemId, projectId) => unwrap(client.post(`/intake/${itemId}/assign`, { project_id: projectId })),
  intakeCreateCo: (itemId) => unwrap(client.post(`/intake/${itemId}/create-co`)),
  coLogDownload: (id, fmt) => opsDownload(`/projects/${id}/change-orders/export/${fmt}`, `Change Order Log.${fmt}`),
  coProposalDownload: (id, coId, number) => opsDownload(`/projects/${id}/change-orders/${coId}/proposal/pdf`, `${number} Change Order Proposal.pdf`),
  // email framework (OPS-3, reused by OPS-4/5/6/9/10)
  emailTemplates: () => unwrap(client.get("/email/templates")),
  saveEmailTemplate: (key, body) => unwrap(client.put(`/email/templates/${key}`, body)),
  resetEmailTemplate: (key) => unwrap(client.post(`/email/templates/${key}/reset`)),
  saveEmailIdentity: (body) => unwrap(client.put("/email/identity", body)),
  emailDraft: (id, key, refId = "") => unwrap(client.get(`/projects/${id}/email-draft/${key}`, { params: { ref_id: refId } })),

  // OPS-6 invoicing
  invoicing: (id) => unwrap(client.get(`/projects/${id}/invoicing`)),
  generateSov: (id) => unwrap(client.post(`/projects/${id}/invoicing/sov/generate`)),
  paySuggestion: (id, periodEnd = "") => unwrap(client.get(`/projects/${id}/invoicing/suggest`, { params: { period_end: periodEnd } })),
  createPayApp: (id, body) => unwrap(client.post(`/projects/${id}/invoicing/pay-applications`, body)),
  payApp: (id, appId) => unwrap(client.get(`/projects/${id}/invoicing/pay-applications/${appId}`)),
  updatePayApp: (id, appId, body) => unwrap(client.patch(`/projects/${id}/invoicing/pay-applications/${appId}`, body)),
  payAppStatus: (id, appId, body) => unwrap(client.post(`/projects/${id}/invoicing/pay-applications/${appId}/status`, body)),
  revisePayApp: (id, appId) => unwrap(client.post(`/projects/${id}/invoicing/pay-applications/${appId}/revise`)),
  portalSheet: (id, appId) => unwrap(client.get(`/projects/${id}/invoicing/pay-applications/${appId}/portal-sheet`)),
  savePortalLabels: (id, labels) => unwrap(client.put(`/projects/${id}/invoicing/portal-labels`, { labels })),
  receivables: () => unwrap(client.get("/dashboard/receivables")),
  retainageWatch: () => unwrap(client.get("/dashboard/retainage")),
  discardPayApp: (id, appId) => unwrap(client.delete(`/projects/${id}/invoicing/pay-applications/${appId}`)),
  emailSend: (id, body) => unwrap(client.post(`/projects/${id}/email-send`, body)),
  emailLog: (id) => unwrap(client.get(`/projects/${id}/email-log`)),
  // procurement (OPS-5)
  purchasing: (id) => unwrap(client.get(`/projects/${id}/purchasing`)),
  purchasingPrice: (id, body) => unwrap(client.post(`/projects/${id}/purchasing/price`, body)),
  po: (id, poId) => unwrap(client.get(`/projects/${id}/purchasing/pos/${poId}`)),
  poPreflight: (id, poId) => unwrap(client.get(`/projects/${id}/purchasing/pos/${poId}/preflight`)),
  poPrepare: (id, body) => unwrap(client.post(`/projects/${id}/purchasing/prepare`, body)),
  poPatch: (id, poId, data) => unwrap(client.patch(`/projects/${id}/purchasing/pos/${poId}`, { data })),
  poIssue: (id, poId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/issue`, body)),
  poAddCoItems: (id, poId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/add-co-items`, body)),
  poAck: (id, poId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/acks`, body)),
  poReceive: (id, poId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/receipts`, body)),
  poVoid: (id, poId) => unwrap(client.delete(`/projects/${id}/purchasing/pos/${poId}`)),
  // vendor quotes on POs (PO-QUOTE)
  poQuotes: (id, poId) => unwrap(client.get(`/projects/${id}/purchasing/quotes`, { params: { po_id: poId || "" } })),
  poQuoteUpload: (id, poId, files, manufacturer = "") => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("po_id", poId || "");
    fd.append("manufacturer", manufacturer || "");
    return unwrap(client.post(`/projects/${id}/purchasing/quotes/upload`, fd,
      { headers: { "Content-Type": "multipart/form-data" } }));
  },
  boardQuotes: (id, params) => unwrap(client.get(`/projects/${id}/purchasing/quotes`, { params })),
  boardQuoteCompare: (id, quoteId, groupKey) => unwrap(client.get(`/projects/${id}/purchasing/quotes/${quoteId}/board-compare`, { params: { group_key: groupKey || "" } })),
  boardQuoteApply: (id, quoteId, body) => unwrap(client.post(`/projects/${id}/purchasing/quotes/${quoteId}/apply-board`, body)),
  poBilled: (id, poId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/billed`, body)),
  poStage: (id, poId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/stage`, body)),
  poShip: (id, poId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/ship`, body)),
  // purchasing@ inbox (PURCH-INBOX)
  purchasingInbox: (params) => unwrap(client.get(`/purchasing/inbox`, { params })),
  purchasingInboxManual: (body) => unwrap(client.post(`/purchasing/inbox/manual`, body)),
  purchasingInboxUpload: (files, projectId, poId = "") => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("project_id", projectId || "");
    fd.append("po_id", poId || "");
    return unwrap(client.post(`/purchasing/inbox/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  purchasingInboxAssign: (itemId, body) => unwrap(client.post(`/purchasing/inbox/${itemId}/assign`, body)),
  purchasingInboxDismiss: (itemId) => unwrap(client.post(`/purchasing/inbox/${itemId}/dismiss`)),
  poQuoteAttach: (id, quoteId, poId) => unwrap(client.post(`/projects/${id}/purchasing/quotes/${quoteId}/attach`, { po_id: poId })),
  poQuoteCompare: (id, poId, quoteId) => unwrap(client.get(`/projects/${id}/purchasing/pos/${poId}/quotes/${quoteId}/compare`)),
  poQuoteApply: (id, poId, quoteId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/quotes/${quoteId}/apply`, body)),
  // group orders (OPS-5B)
  groupCandidates: () => unwrap(client.get(`/purchasing/group-orders/candidates`)),
  groupOrders: () => unwrap(client.get(`/purchasing/group-orders`)),
  groupOrder: (gid) => unwrap(client.get(`/purchasing/group-orders/${gid}`)),
  groupCreate: (body) => unwrap(client.post(`/purchasing/group-orders`, body)),
  groupRfqPreview: (gid) => unwrap(client.get(`/purchasing/group-orders/${gid}/rfq-preview`)),
  groupSend: (gid, body) => unwrap(client.post(`/purchasing/group-orders/${gid}/send`, body)),
  groupQuote: (gid, body) => unwrap(client.post(`/purchasing/group-orders/${gid}/quote`, body)),
  groupOrderPlace: (gid, body) => unwrap(client.post(`/purchasing/group-orders/${gid}/order`, body)),
  groupAck: (gid, body) => unwrap(client.post(`/purchasing/group-orders/${gid}/acks`, body)),
  groupReceive: (gid, body) => unwrap(client.post(`/purchasing/group-orders/${gid}/receipts`, body)),
  groupRemoveJob: (gid, pid) => unwrap(client.delete(`/purchasing/group-orders/${gid}/jobs/${pid}`)),
  groupVoid: (gid) => unwrap(client.delete(`/purchasing/group-orders/${gid}`)),
  groupSettings: () => unwrap(client.get(`/purchasing/group-orders/settings`)),
  groupSettingsPatch: (data) => unwrap(client.patch(`/purchasing/group-orders/settings`, { data })),
  groupMonitorRun: () => unwrap(client.post(`/purchasing/group-orders/monitor/run`, {})),
  groupSavings: () => unwrap(client.get(`/purchasing/group-orders/savings/summary`)),
  purchasingOverview: () => unwrap(client.get(`/purchasing/overview`)),
  // PRC-REV — procurement review gate
  poReview: (id, poId) => unwrap(client.get(`/projects/${id}/purchasing/pos/${poId}/review`)),
  poReviewRun: (id, poId, body = {}) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/review/run`, body)),
  poReviewSkip: (id, poId) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/review/skip`, {})),
  poReviewAck: (id, poId, body) => unwrap(client.post(`/projects/${id}/purchasing/pos/${poId}/review/acknowledge`, body)),
  poReviewPacket: (id, poId) => unwrap(client.get(`/projects/${id}/purchasing/pos/${poId}/review/packet`)),
  reviewMetrics: () => unwrap(client.get(`/purchasing/review/metrics`)),
  reviewSettingsPatch: (body) => unwrap(client.patch(`/purchasing/review/settings`, body)),
  quoteFileOpen: (fileId) => opsOpen(`/api/estimating/quote-files/${fileId}`),
  poPdf: (id, poId) => opsOpen(`/projects/${id}/purchasing/pos/${poId}/pdf`),
  poReceipts: (id) => unwrap(client.get(`/projects/${id}/purchasing/receipts`)),
  poLogPdf: (id) => opsDownload(`/projects/${id}/purchasing/export/pdf`, "Procurement Log.pdf"),
  poLogExport: (id) => opsDownload(`/projects/${id}/purchasing/export/xlsx`, "Procurement Log.xlsx"),
  manufacturers: () => unwrap(client.get(`/purchasing/manufacturers`)),
  manufacturerPatch: (mid, data) => unwrap(client.patch(`/purchasing/manufacturers/${mid}`, { data })),
  purchasingSettings: () => unwrap(client.get(`/purchasing/settings`)),
  purchasingSettingsPatch: (body) => unwrap(client.patch(`/purchasing/settings`, body)),
  // submittals (OPS-4)
  submittals: (id) => unwrap(client.get(`/projects/${id}/submittals`)),
  submittalLog: (id) => unwrap(client.get(`/projects/${id}/submittals/log`)),
  submittalLogReturn: (id, pkgId, form) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/log-return`, form, { headers: { "Content-Type": "multipart/form-data" } })),
  submittalConfirmStatus: (id, pkgId, body) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/confirm-status`, body)),
  submittalAction: (id, pkgId, body) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/action`, body)),
  submittalPendingConfirm: (id, pkgId, body) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/pending-return/confirm`, body)),
  submittalPendingDismiss: (id, pkgId) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/pending-return/dismiss`, {})),
  submittalUnmatchedReturns: (id) => unwrap(client.get(`/projects/${id}/submittals/unmatched-returns`)),
  submittalLinkUnmatched: (id, intakeId, pkgId) => unwrap(client.post(`/projects/${id}/submittals/unmatched-returns/${intakeId}/link`, { package_id: pkgId })),
  submittalGlobalSettings: () => unwrap(client.get(`/submittal-settings`)),
  submittalReviewSettings: (body) => unwrap(client.put(`/submittal-settings/review`, body)),
  submittalReview: (id, pkgId) => unwrap(client.get(`/projects/${id}/submittals/${pkgId}/review`)),
  submittalRescan: (id, pkgId) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/review/rescan`, {})),
  submittalDirective: (id, pkgId, key, body = {}) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/review/directives/${key}`, body)),
  submittalChecklist: (id, pkgId, body) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/review/checklist`, body)),
  submittalReviewPageUrl: (id, pkgId, side, page) =>
    `${window.location.origin}/api/operations/projects/${id}/submittals/${pkgId}/review/page?side=${side}&page=${page}&dpi=96`,
  submittalStampTerms: (terms) => unwrap(client.put(`/submittal-settings/stamp-terms`, { stamp_terms: terms })),
  submittal: (id, pkgId) => unwrap(client.get(`/projects/${id}/submittals/${pkgId}`)),
  submittalDiscrepancies: (id) => unwrap(client.get(`/projects/${id}/submittals/discrepancies`)),
  submittalGenerate: (id) => unwrap(client.post(`/projects/${id}/submittals/generate`, {})),
  submittalCreate: (id, body) => unwrap(client.post(`/projects/${id}/submittals`, body)),
  submittalDelete: (id, pkgId) => unwrap(client.delete(`/projects/${id}/submittals/${pkgId}`)),
  submittalPatch: (id, pkgId, data) => unwrap(client.patch(`/projects/${id}/submittals/${pkgId}`, { data })),
  submittalStatus: (id, pkgId, body) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/status`, body)),
  submittalSwap: (id, pkgId, body) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/swaps`, body)),
  submittalFrsProposals: (id, pkgId) => unwrap(client.get(`/projects/${id}/submittals/${pkgId}/frs/proposals`)),
  submittalFrsApply: (id, pkgId, body) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/frs/apply`, body)),
  submittalReturned: (id, form) => unwrap(client.post(`/projects/${id}/submittals/returned`, form, { headers: { "Content-Type": "multipart/form-data" } })),
  submittalLogDownload: (id) => opsDownload(`/projects/${id}/submittals/export/xlsx`, "Submittal Log.xlsx"),
  // SUB — keying, send, returns, downstream reads
  submittalSettings: (id, body) => unwrap(client.post(`/projects/${id}/submittals/settings`, body)),
  submittalUnlock: (id, pkgId, reason) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/unlock`, { reason })),
  submittalMarkSubmitted: (id, pkgId, body) => unwrap(client.post(`/projects/${id}/submittals/${pkgId}/mark-submitted`, body)),
  submittalPdfDownload: (id, pkgId, name) => opsDownload(`/projects/${id}/submittals/${pkgId}/download`, name || "Submittal.pdf"),
  submittalZipDownload: (id, scope = "unsent") => opsDownload(`/projects/${id}/submittals/download-zip?scope=${scope}`, "Submittals.zip"),
  submittalSubmitAllDraft: (id) => unwrap(client.get(`/projects/${id}/submittals/submit-all/draft`)),
  submittalSubmitAll: (id, body) => unwrap(client.post(`/projects/${id}/submittals/submit-all`, body)),
  submittalApprovedByScope: (id) => unwrap(client.get(`/projects/${id}/submittals/approved-by-scope`)),
};

export const pct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

export const money = (n) => {
  const v = Number(n || 0);
  const body = `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return v < 0 ? `-${body}` : body;
};

export const STATUS_TONE = {
  estimate: "bg-slate-100 text-slate-700 border-slate-200",
  ready: "bg-amber-50 text-amber-700 border-amber-200",
  open: "bg-emerald-50 text-emerald-700 border-emerald-200",
  on_hold: "bg-orange-50 text-orange-700 border-orange-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};
